import { PriceStyleVariant, Variant } from '@app/components/Price/Price';
import type { ItemPrice, VolumeDiscountThreshold } from '@app/types/price';
import type { DiscountType } from '@redbubble/next-analytics/gtm';
import { round } from 'lodash';
import { Price } from '../components/Price';

export const getDiscountTypeAndAmount = (
  thresholds: VolumeDiscountThreshold[],
  price?: ItemPrice | null
): {
  discountType: DiscountType;
  variant: Variant;
  discountedAmount: number;
  originalAmount: number;
} | null => {
  if (!price) {
    return null;
  }

  // Always show discounted (strikethrough) price if available
  if (price.discount?.amount > 0) {
    return {
      discountType: 'promotion',
      variant: 'defaultPriceStyle',
      discountedAmount: price.discount.amount,
      originalAmount: price.amount,
    };
  }

  // Next show volume bundle discounted price if available
  if (thresholds.length > 0) {
    const maximumVolumeDiscountPercentageOff = Math.max(
      ...thresholds.map(({ percentOff }) => percentOff),
      0
    );
    const fromAmount = round(
      (price.amount * (100 - maximumVolumeDiscountPercentageOff)) / 100,
      2
    );
    return {
      discountType: 'volume',
      variant: 'fromPriceStyle',
      discountedAmount: fromAmount,
      originalAmount: price.amount,
    };
  }

  // Fallback to returning the normal prices
  return {
    discountType: 'none',
    variant: 'defaultPriceStyle',
    discountedAmount: price.amount,
    originalAmount: price.amount,
  };
};

export const getPriceComponent = (
  thresholds: VolumeDiscountThreshold[],
  price: ItemPrice | null | undefined,
  styleVariant?: PriceStyleVariant
): React.ReactNode => {
  if (!price) {
    return null;
  }

  const priceTypeAndAmount = getDiscountTypeAndAmount(thresholds, price);
  if (!priceTypeAndAmount) {
    return null;
  }

  const { discountType, originalAmount, discountedAmount } = priceTypeAndAmount;

  switch (discountType) {
    // Discount calculations happen in Price component for custom discount
    case 'promotion':
      return (
        <Price
          size="small"
          discount={price.discount}
          amount={originalAmount}
          currency={price.currency}
          hideVATOverride
          styleVariant={styleVariant}
        />
      );

    case 'volume':
      return (
        <Price
          variant="fromPriceStyle"
          size="small"
          amount={discountedAmount}
          currency={price.currency}
          hideVATOverride
          styleVariant={styleVariant}
        />
      );

    case 'none':
      return (
        <Price
          size="small"
          amount={originalAmount}
          currency={price.currency}
          hideVATOverride
          styleVariant={styleVariant}
        />
      );

    default:
      return null;
  }
};
